import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  defaultPoItemValueV2,
  PoItemV2,
} from "../../../model/purchase_order_model";
import { numberRegEx } from "../../../../../utils/constant";
import { ItemModelV2 } from "../../../../inventory/models/item_model";
import { Project } from "../../../../inventory/models/project_model";
import { usePurchaseOrder } from "../../../contexts/po-creation-context";
import { generate_id } from "../../../../inventory/journals/destination_stock_voucher/components/item_row_status";
import {
  fetchItemWac,
  fetchWACPrice,
} from "../../service/purchase_order_repository";
import { ACTION, MODULE } from "../../../../../utils/enums";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { buildTaxContext, computeLineTaxDisplay } from "./poLineItemTax";
import { LineTaxDisplay, POLineItemCardProps } from "./types";

/**
 * All line-item behavior in one place — validation, item/project selection,
 * qty/price/budget handling, Job Work line-type + DC/WAC, and per-line tax
 * display. The web and mobile card shells are pure presentation over this hook,
 * so their functionality is identical by construction.
 */
export const usePOLineItem = (props: POLineItemCardProps) => {
  const {
    mode,
    variant,
    data: propData,
    existingItems,
    allowedBudget,
    inwardStatus,
    onSubmit,
    onDelete,
  } = props;

  const { defaultProject, setDefaultProject, purchaseOrder } =
    usePurchaseOrder();
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const isAddRow = !propData;
  const readOnly = mode === "view" || mode === "approve";
  const isJobWork = variant === "jobwork";

  const [data, setData] = useState<PoItemV2>({ ...defaultPoItemValueV2 });
  const [editable, setEditable] = useState(false);
  const [canAdd, setCanAdd] = useState(true);
  const [isPOAmender, setIsPOAmender] = useState(false);
  const [itemWac, setItemWac] = useState<number | null>(null);

  useEffect(() => {
    setEditable(isAddRow && !readOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPOAmender(
      isAuthorised({ module: MODULE.po_amender, action: ACTION.UPDATE }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorised]);

  useEffect(() => {
    if (propData) {
      setData({ ...propData });
    } else if (existingItems?.length === 0 && defaultProject) {
      setData((prev) => ({
        ...prev,
        project_id: defaultProject.id,
        project: defaultProject,
      }));
    }
  }, [propData, defaultProject, existingItems]);

  // Job Work: auto-fetch WAC price once item + DC are chosen.
  useEffect(() => {
    if (!isJobWork) return;
    const itemCode = data?.item?.item_code;
    const dcId = data?.dc_reference_id;
    if (!itemCode || !dcId) {
      setItemWac(null);
      return;
    }
    let active = true;
    (async () => {
      const wacData = await fetchWACPrice(itemCode, dcId);
      if (!active) return;
      if (wacData?.data) {
        setData((prev) => ({ ...prev, price: wacData?.data?.wac_value || 0 }));
      }
      setItemWac(wacData?.data?.wac_value ?? null);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobWork, data?.dc_reference_id, data?.item?.item_code]);

  // Regular PO lines: fetch WAC for the selected item so we can flag price
  // entries that deviate significantly from it (advisory only).
  useEffect(() => {
    if (isJobWork || readOnly) return;
    const itemCode = data?.item?.item_code;
    if (!itemCode) {
      setItemWac(null);
      return;
    }
    let active = true;
    (async () => {
      const wacData = await fetchItemWac(itemCode);
      if (active) setItemWac(wacData?.data?.wac_value ?? null);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobWork, readOnly, data?.item?.item_code]);

  const wacDeviationPct =
    itemWac && itemWac > 0 && data.price
      ? ((data.price - itemWac) / itemWac) * 100
      : null;
  const showWacWarning =
    wacDeviationPct !== null && Math.abs(wacDeviationPct) >= 10;

  // Job Work: derive payable / inventory-impact / gst flags from line_type.
  useEffect(() => {
    if (!isJobWork) return;
    setData((prev) =>
      prev.line_type === "service"
        ? {
            ...prev,
            is_inventory_line: false,
            is_payable: true,
            gst_applicable: true,
          }
        : {
            ...prev,
            is_inventory_line: true,
            is_payable: false,
            gst_applicable: false,
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobWork, data.line_type]);

  const onDone = () => {
    if (
      isJobWork &&
      data.line_type === "service" &&
      data.item?.type !== "Service"
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please select service item type for service line item!!!",
      });
      return;
    }
    if (!data.item?.item_code || data.price < 0 || !data.unit_no) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Price and Qty",
      });
      return;
    }
    const isDuplicate = existingItems?.some(
      (item) =>
        item.item?.item_code === data.item?.item_code &&
        Number(item.price) === Number(data.price) &&
        item.id !== data.id,
    );
    if (isDuplicate) {
      showToast({
        type: ToastType.error,
        text: "Same Item Code with same Price already exists!",
      });
      return;
    }
    if (!defaultProject) {
      showToast({ type: ToastType.warning, text: "Please select a Project" });
      return;
    }
    if (
      data.project === undefined ||
      (data.project === null && defaultProject)
    ) {
      data.project_id = defaultProject.id;
      data.project = defaultProject;
    }
    onSubmit(data);
  };

  const handleItemSelect = (selectedItem: ItemModelV2) => {
    setCanAdd(true);
    setData((prev) => ({
      ...prev,
      id: prev.id ?? generate_id(),
      item: selectedItem,
      item_id: selectedItem.id,
    }));
  };

  const handleProjectSelection = (selectedProject: Project) => {
    if (!selectedProject) {
      showToast({ type: ToastType.error, text: "Please Select Project" });
      return;
    }
    if (!defaultProject && existingItems?.length === 0) {
      setDefaultProject(selectedProject);
    }
    setData((prev) => ({
      ...prev,
      project_id: selectedProject.id,
      project: selectedProject,
    }));
  };

  const onQtyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    if (val === "" || !numberRegEx.test(e.target.value)) return;
    setData((prev) => ({ ...prev, unit_no: Number(val) }));
  };

  const onPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    if (allowedBudget && Number(val) > allowedBudget) {
      showToast({ type: ToastType.error, text: "Total Budget Exceeded" });
      return;
    }
    if (val === "" || !numberRegEx.test(e.target.value)) return;
    setData((prev) => ({ ...prev, price: Number(val) }));
  };

  const onLineTypeChange = (line_type: string) =>
    setData((prev) => ({ ...prev, line_type }));

  const onDcSelect = (selectedDC: { id: string }) =>
    setData((prev) => ({ ...prev, dc_reference_id: selectedDC.id }));

  const cancelEdit = () => {
    if (propData) setData({ ...propData });
    setEditable(false);
  };

  const remove = () => {
    if (onDelete) onDelete(data);
  };

  const tax: LineTaxDisplay = useMemo(
    () => computeLineTaxDisplay(data, buildTaxContext(purchaseOrder), variant),
    [data, purchaseOrder, variant],
  );

  // POAmender / inward gating applied only in the Update-standard existing-row
  // context (the only caller that passes inwardStatus), matching PoItemRowDetailV2.
  const enforceUpdateGating = inwardStatus !== undefined;
  const itemSearchDisabled = enforceUpdateGating
    ? (!isPOAmender && inwardStatus !== "Pending") || !editable || readOnly
    : !editable || readOnly;
  const qtyDisabled = enforceUpdateGating
    ? !isPOAmender || !editable
    : !editable;

  return {
    data,
    setData,
    editable,
    setEditable,
    readOnly,
    isAddRow,
    isJobWork,
    canAdd,
    isPOAmender,
    defaultProject,
    tax,
    itemWac,
    wacDeviationPct,
    showWacWarning,
    itemSearchDisabled,
    qtyDisabled,
    onDone,
    cancelEdit,
    remove,
    handleItemSelect,
    handleProjectSelection,
    onQtyChange,
    onPriceChange,
    onLineTypeChange,
    onDcSelect,
  };
};

export default usePOLineItem;
